import React, { useState, useCallback } from 'react';
import { useParams, useHistory } from 'react-router-dom';
import { makeStyles } from '@mui/styles';
import {
  Typography,
  Grid,
  Chip,
  Button,
  Alert,
  Box,
  CircularProgress,
  Breadcrumbs,
  Link,
  Skeleton,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import HistoryIcon from '@mui/icons-material/History';
import { useQuery } from '@tanstack/react-query';
import { getSupportDevices } from 'apis/supportAPIs';
import {
  SupportRoleGuard,
  CommandStatusDrawer,
  FetchTransactions,
  CaptureScreenshot,
  ExportPrinterSettings,
  ImportMenu,
} from './components';

const useStyles = makeStyles((theme) => ({
  root: {
    padding: theme.spacing(3),
    flexGrow: 1,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(2),
    marginBottom: theme.spacing(1),
    flexWrap: 'wrap',
  },
  headerInfo: {
    flex: 1,
  },
  badges: {
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',
    flexWrap: 'wrap',
  },
  tilesContainer: {
    marginTop: theme.spacing(3),
  },
  piiWarning: {
    marginBottom: theme.spacing(2),
  },
  breadcrumbs: {
    marginBottom: theme.spacing(2),
  },
  uiProgress: {
    position: 'fixed',
    zIndex: 1000,
    left: '50%',
    top: '35%',
  },
}));

const DeviceDetail = () => {
  const classes = useStyles();
  const { restaurantId, deviceId } = useParams();
  const history = useHistory();

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeCommandId, setActiveCommandId] = useState(null);
  const [activeCommandType, setActiveCommandType] = useState(null);
  const [lastCommandPayload, setLastCommandPayload] = useState(null);

  const {
    data: devices,
    isLoading,
    isError,
    error,
  } = useQuery({
    queryKey: ['supportDevices', restaurantId],
    queryFn: () => getSupportDevices(restaurantId),
    staleTime: 30000,
  });

  const device = devices?.find?.((d) => d.deviceId === deviceId);
  const restaurantName = device?.restaurantName || restaurantId;

  const handleCommandStart = useCallback((commandId, commandType) => {
    setActiveCommandId(commandId);
    setActiveCommandType(commandType);
    setDrawerOpen(true);
  }, []);

  const handleRetry = useCallback(async () => {
    if (lastCommandPayload) {
      const { postSupportCommand } = await import('apis/supportAPIs');
      try {
        const { commandId } = await postSupportCommand(lastCommandPayload);
        setActiveCommandId(commandId);
      } catch {
        // drawer will show error state
      }
    }
  }, [lastCommandPayload]);

  if (isLoading) {
    return (
      <SupportRoleGuard>
        <div className={classes.root}>
          <Skeleton width={300} height={40} />
          <Skeleton width={200} height={30} sx={{ mt: 1 }} />
          <Grid container spacing={3} sx={{ mt: 3 }}>
            {[...Array(4)].map((_, i) => (
              <Grid item xs={12} sm={6} md={3} key={i}>
                <Skeleton variant="rounded" height={200} />
              </Grid>
            ))}
          </Grid>
        </div>
      </SupportRoleGuard>
    );
  }

  if (isError) {
    return (
      <SupportRoleGuard>
        <div className={classes.root}>
          <Alert severity="error">{error?.message || 'Failed to load device'}</Alert>
        </div>
      </SupportRoleGuard>
    );
  }

  if (!device) {
    return (
      <SupportRoleGuard>
        <div className={classes.root}>
          <Alert severity="warning">Device not found.</Alert>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => history.push('/support')}
            sx={{ mt: 2 }}
          >
            Back to Support
          </Button>
        </div>
      </SupportRoleGuard>
    );
  }

  return (
    <SupportRoleGuard>
      <div className={classes.root}>
        <Breadcrumbs className={classes.breadcrumbs} aria-label="Support breadcrumb">
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              history.push('/support');
            }}
          >
            Support
          </Link>
          <Link
            color="inherit"
            href="#"
            onClick={(e) => {
              e.preventDefault();
              history.push('/support');
            }}
          >
            {restaurantName}
          </Link>
          <Typography color="textPrimary">{device.vendorIdShort || deviceId}</Typography>
        </Breadcrumbs>

        <Alert severity="info" className={classes.piiWarning}>
          All actions are audit-logged. Artifacts may contain PII.
        </Alert>

        <div className={classes.header}>
          <div className={classes.headerInfo}>
            <Typography variant="h4">{restaurantName}</Typography>
            <Typography variant="subtitle1" color="textSecondary">
              Device: {device.vendorIdShort || deviceId}
            </Typography>
          </div>
          <div className={classes.badges}>
            <Chip
              icon={
                <FiberManualRecordIcon
                  sx={{
                    fontSize: 12,
                    color: device.isOnline ? 'success.main' : 'grey.500',
                  }}
                />
              }
              label={device.isOnline ? 'Online' : 'Offline'}
              variant="outlined"
              size="small"
            />
            {device.appVersion && (
              <Chip label={`v${device.appVersion}`} variant="outlined" size="small" />
            )}
            <Button
              size="small"
              startIcon={<HistoryIcon />}
              onClick={() => history.push(`/support/${restaurantId}/audit`)}
            >
              Audit Log
            </Button>
          </div>
        </div>

        <Grid container spacing={3} className={classes.tilesContainer}>
          <Grid item xs={12} sm={6} lg={3}>
            <FetchTransactions
              restaurantId={restaurantId}
              deviceId={deviceId}
              onCommandStart={handleCommandStart}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <CaptureScreenshot
              restaurantId={restaurantId}
              deviceId={deviceId}
              onCommandStart={handleCommandStart}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ExportPrinterSettings
              restaurantId={restaurantId}
              deviceId={deviceId}
              onCommandStart={handleCommandStart}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={3}>
            <ImportMenu
              restaurantId={restaurantId}
              deviceId={deviceId}
              merchantName={restaurantName}
              onCommandStart={handleCommandStart}
            />
          </Grid>
        </Grid>

        <CommandStatusDrawer
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          commandId={activeCommandId}
          commandType={activeCommandType}
          restaurantId={restaurantId}
          deviceId={deviceId}
          onRetry={handleRetry}
        />
      </div>
    </SupportRoleGuard>
  );
};

export default DeviceDetail;
